/**
 * Eine Veröffentlichung: Titel, Papier und die Songs darauf.
 *
 * Aufgebaut wie die Setlist-Ansicht, weil es dasselbe Muster ist: eine
 * geordnete Liste von Song-Ids. Deshalb auch dieselbe Umsortier-Mechanik
 * (ui/reorder.ts) statt einer zweiten Eigenentwicklung.
 */
import type { View } from '../app.ts';
import { navigate } from '../app.ts';
import type { Release, ReleaseKind, Song } from '../types.ts';
import {
  RELEASE_KINDS,
  deleteRelease,
  emptySong,
  getMyArtwork,
  getRelease,
  listSongs,
  releaseKindLabel,
  saveRelease,
  saveSong,
  setArtworkVisibility,
  setReleaseArtwork,
  visibleArtwork,
} from '../db/repo.ts';
import type { DesignVisibility } from '../db/repo.ts';
import { shrinkImage } from '../ui/image.ts';
import { askConfirm, askText } from '../ui/ask.ts';
import { SEPARATOR, withContextMenu } from '../ui/context-menu.ts';
import { pickImageFile, renderArtwork } from '../ui/artwork.ts';
import { makeReorderable, moveItem } from '../ui/reorder.ts';
import { button, h, select, textInput } from '../ui/dom.ts';
import { icon } from '../ui/icons.ts';
import { toast } from '../ui/toast.ts';
import { songMetaLine } from './library.ts';
import { exportRelease } from '../features/pdf/dialog.ts';

export async function renderReleaseDetail(params: Record<string, string>): Promise<View> {
  const existing = await getRelease(params.id as string);
  if (!existing) {
    return {
      title: 'Nicht gefunden',
      back: '#/',
      body: h('div', { class: 'empty', text: 'Diese Veröffentlichung wurde geloescht.' }),
    };
  }

  let release: Release = existing;
  /** Mein Cover-Eintrag: privat (nur ich) oder oeffentlich (am Release). */
  let mine = await getMyArtwork(existing.id);
  const shownArtwork = () => visibleArtwork(release, mine);
  const songs = await listSongs(release.projectId);
  const byId = new Map(songs.map((song) => [song.id, song]));

  /** Speichert eine Änderung und aktualisiert die Kopie im Speicher. */
  async function touch(patch: Partial<Release>): Promise<void> {
    release = await saveRelease({ ...release, ...patch });
  }

  /* ------------------------------------------------------- Das Artwork */

  const artworkBox = h('div', { class: 'release__pad' });

  function paintPad(): void {
    artworkBox.textContent = '';
    artworkBox.append(
      renderArtwork({
        image: shownArtwork(),
        label: release.title || 'Ohne Titel',
        badge: release.songIds.length > 0 ? String(release.songIds.length) : undefined,
        emblem: release.kind === 'single' ? '●' : release.kind === 'ep' ? '◎' : '◉',
      }),
    );
    removeArtworkButton.hidden = !shownArtwork();
  }

  /** Bild waehlen und als Cover ablegen; ersetzt eine vorhandene Hülle. */
  async function chooseArtwork(): Promise<void> {
    const file = await pickImageFile();
    if (!file) return;
    // Verkleinert: ein privates Cover liegt bei den Einstellungen, und die
    // haben in der Cloud eine Groessengrenze.
    const image = await shrinkImage(file, 1200, 500_000);
    release = await setReleaseArtwork(release, image);
    mine = await getMyArtwork(release.id);
    paintPad();
    toast(mine?.visibility === 'private' ? 'Cover gesichert – nur für dich sichtbar' : 'Cover gesichert');
  }

  async function removeArtwork(): Promise<void> {
    if (!shownArtwork()) return;
    if (!(await askConfirm({ title: 'Cover entfernen?', text: 'Die Hülle wird wieder leer.', confirm: 'Entfernen', danger: true }))) return;
    release = await setReleaseArtwork(release, undefined);
    mine = await getMyArtwork(release.id);
    paintPad();
  }

  /* ------------------------------------------------ Privat / Öffentlich */

  const visSwitch = h('div', { class: 'segmented release__vis', attrs: { role: 'group', 'aria-label': 'Wer sieht das Cover' } });
  const currentVis = (): DesignVisibility => mine?.visibility ?? 'public';
  const visButtons = (['private', 'public'] as DesignVisibility[]).map((value) => {
    const el = h('button', {
      class: 'segmented__item',
      text: value === 'private' ? 'Privat' : 'Öffentlich',
      attrs: { type: 'button', title: value === 'private' ? 'Nur ich sehe dieses Cover' : 'Alle in der Band sehen dieses Cover' },
      on: { click: () => void switchVisibility(value) },
    });
    visSwitch.append(el);
    return { value, el };
  });
  function markVisibility(): void {
    for (const { value, el } of visButtons) {
      el.classList.toggle('segmented__item--on', value === currentVis());
      el.setAttribute('aria-pressed', String(value === currentVis()));
    }
  }
  markVisibility();

  async function switchVisibility(next: DesignVisibility): Promise<void> {
    if (next === currentVis()) return;
    const ok = await askConfirm({
      title: next === 'public' ? 'Cover veröffentlichen?' : 'Cover auf privat stellen?',
      text:
        next === 'public'
          ? 'Bist du dir sicher? Alle in der Band sehen dann dein Cover für diese Veröffentlichung.'
          : 'Bist du dir sicher? Dann siehst nur du dieses Cover. Die anderen sehen das vorherige gemeinsame Cover – oder eine leere Hülle, falls es keins gab.',
      confirm: next === 'public' ? 'Veröffentlichen' : 'Auf privat stellen',
    });
    if (!ok) return;
    release = await setArtworkVisibility(release, next);
    mine = await getMyArtwork(release.id);
    markVisibility();
    paintPad();
    toast(next === 'public' ? 'Cover ist jetzt öffentlich' : 'Cover ist jetzt privat');
  }

  /* ------------------------------------------------------- Die Angaben */

  const titleInput = textInput({
    value: release.title,
    placeholder: 'Titel',
    onInput: (value) => void touch({ title: value }).then(paintPad),
  });

  const yearInput = textInput({
    value: release.year,
    placeholder: 'z. B. 2026',
    onInput: (value) => void touch({ year: value }).then(paintPad),
  });

  const kindSelect = select(
    RELEASE_KINDS.map((k) => ({ value: k.value, label: k.label })),
    release.kind,
    (value) => void touch({ kind: value as ReleaseKind }),
  );

  const artworkButton = button('Cover wählen', () => void chooseArtwork(), { class: 'btn' });
  const removeArtworkButton = button('Cover entfernen', () => void removeArtwork(), {
    class: 'btn btn--ghost',
  });
  paintPad();

  /* ----------------------------------------------------------- Songs */

  const listEl = h('ul', { class: 'list' });

  function paintSongs(): void {
    listEl.textContent = '';
    const chosen = release.songIds.map((id) => byId.get(id)).filter((s): s is Song => !!s);

    if (chosen.length === 0) {
      listEl.append(
        h(
          'li',
          { class: 'empty' },
          h('p', { class: 'empty__title', text: 'Noch keine Songs' }),
          h('p', { text: 'Füg unten vorhandene Songs hinzu oder leg einen neuen an.' }),
        ),
      );
      return;
    }

    chosen.forEach((song, index) => {
      listEl.append(
        withContextMenu(h(
          'li',
          { class: 'list-item', data: { id: song.id } },
          h('span', { class: 'list-item__index', text: String(index + 1) }),
          h(
            'button',
            {
              class: 'list-item__main',
              attrs: { type: 'button' },
              on: { click: () => navigate(`#/sheet/${song.id}`) },
            },
            h('span', { class: 'list-item__title', text: song.title || 'Ohne Titel' }),
            h('span', { class: 'list-item__meta', text: songMetaLine(song) }),
          ),
          h(
            'div',
            { class: 'list-item__actions' },
            button(
              'Entfernen',
              () => {
                void touch({ songIds: release.songIds.filter((id) => id !== song.id) }).then(() => {
                  paintSongs();
                  paintPad();
                  paintPicker();
                });
              },
              { class: 'btn btn--ghost btn--small', title: 'Vom Release nehmen' },
            ),
          ),
        ), song.title || 'Ohne Titel', () => [
          { label: 'Öffnen', action: () => navigate(`#/sheet/${song.id}`) },
          { label: 'Bearbeiten', action: () => navigate(`#/song/${song.id}`) },
          SEPARATOR,
          {
            label: 'Aus dem Album nehmen',
            action: () =>
              void touch({ songIds: release.songIds.filter((id) => id !== song.id) }).then(() => {
                paintSongs();
                paintPad();
                paintPicker();
              }),
          },
        ]),
      );
    });

    makeReorderable(listEl, {
      onReorder: (from, to) => {
        void touch({ songIds: moveItem(release.songIds, from, to) }).then(paintSongs);
      },
    });
  }

  /* ------------------------------------------------- Songs hinzufügen */

  const pickerEl = h('div', { class: 'release__picker' });

  /**
   * Ganz neuer Song direkt aus dem Album: anlegen, gleich hinten anhaengen
   * und in den Editor springen. Er gehoert wie jeder Song dem Projekt.
   */
  async function createSong(): Promise<void> {
    const title = await askText({ title: 'Neuer Song', label: 'Titel', placeholder: 'Wie heisst der Song?' });
    if (title === null) return;
    const song = await saveSong(emptySong(release.projectId, { title }));
    await touch({ songIds: [...release.songIds, song.id] });
    navigate(`#/song/${song.id}`);
  }

  const newSongButton = button('+ Neuer Song', () => void createSong(), { class: 'btn btn--primary btn--small' });

  function paintPicker(): void {
    pickerEl.textContent = '';
    const open = songs.filter((song) => !release.songIds.includes(song.id));

    if (open.length === 0) {
      pickerEl.append(
        h('p', {
          class: 'muted',
          text: songs.length === 0 ? 'Im Projekt gibt es noch keine Songs.' : 'Alle vorhandenen Songs sind drauf.',
        }),
        newSongButton,
      );
      return;
    }
    pickerEl.append(newSongButton);
    for (const song of open) {
      pickerEl.append(
        button(
          song.title || 'Ohne Titel',
          () => {
            void touch({ songIds: [...release.songIds, song.id] }).then(() => {
              paintSongs();
              paintPad();
              paintPicker();
            });
          },
          { class: 'chip', title: 'Hinzufügen' },
        ),
      );
    }
  }

  paintSongs();
  paintPicker();

  const body = h(
    'div',
    { class: 'release' },
    h(
      'div',
      { class: 'release__head' },
      artworkBox,
      h(
        'div',
        { class: 'release__fields' },
        h('label', { class: 'field' }, h('span', { class: 'field__label', text: 'Titel' }), titleInput),
        h(
          'div',
          { class: 'grid-2' },
          h('label', { class: 'field' }, h('span', { class: 'field__label', text: 'Art' }), kindSelect),
          h('label', { class: 'field' }, h('span', { class: 'field__label', text: 'Jahr' }), yearInput),
        ),
        h('div', { class: 'btn-row' }, artworkButton, removeArtworkButton),
        h('div', { class: 'field' }, h('span', { class: 'field__label', text: 'Cover sichtbar für' }), visSwitch),
      ),
    ),
    h('h2', { class: 'card__title', text: 'Songs' }),
    listEl,
    h('h2', { class: 'card__title', text: 'Hinzufügen' }),
    pickerEl,
  );

  return {
    title: release.title || 'Ohne Titel',
    subtitle: releaseKindLabel(release.kind),
    back: '#/',
    actions: [
      button('PDF', () => void exportRelease({ ...release, artwork: shownArtwork() }), { class: 'btn btn--ghost', title: 'Als PDF exportieren' }),
      h(
        'button',
        {
          class: 'btn btn--icon',
          attrs: { type: 'button', title: 'Löschen', 'aria-label': 'Veröffentlichung löschen' },
          on: {
            click: async () => {
              if (
                !confirm(
                  `"${release.title || 'Ohne Titel'}" löschen?\n\nDie Songs bleiben erhalten.`,
                )
              ) {
                return;
              }
              await deleteRelease(release.id);
              toast('Veröffentlichung gelöscht');
              navigate('#/');
            },
          },
        },
        icon('trash'),
      ),
    ],
    body,
  };
}
